import { gql, useMutation, useQuery } from '@apollo/client'
import { Grid, Stack, Button, Paper } from '@mui/material'
import { Formik, Form, Field } from 'formik'
import { TextField } from 'formik-mui'
import I18n from 'i18n-js'
import { useSnackbar } from 'notistack'
import { useParams } from 'react-router'
import * as Yup from 'yup'

import useCurrentRetailerAccount from '@src/lib/useCurrentRetailerAccount'

const validationSchema = Yup.object().shape({
  website: Yup.string().required('Required'),
})

const GET_RETAILER_WEBSITE = gql`
  query GetRetailer($getRetailerId: ID!) {
    getRetailer(id: $getRetailerId) {
      id
      name
      website
    }
  }
`

const UPDATE_RETAILER_WEBSITE = gql`
  mutation UpdateRetailer(
    $updateRetailerId: ID!
    $retailer: UpdateRetailerParams!
  ) {
    updateRetailer(id: $updateRetailerId, retailer: $retailer) {
      id
      name
      website
    }
  }
`

const RetailerSettingsBrandSettingsWebsite: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const { ensureCurrentRetailerAccount } = useCurrentRetailerAccount()
  const params = useParams<{ brandSlug: string }>()
  const currentRetailerId = ensureCurrentRetailerAccount?.retailers?.find(
    (x) => String(x.brand?.slug) === String(params.brandSlug),
  )?.id

  const { data, loading } = useQuery(GET_RETAILER_WEBSITE, {
    variables: {
      getRetailerId: currentRetailerId,
    },
  })

  const retailer = loading ? false : data?.getRetailer

  const [updateRetailer] = useMutation(UPDATE_RETAILER_WEBSITE)

  return (
    <Paper sx={{ padding: 4 }}>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Stack spacing={2}>
            {retailer && (
              <Formik
                enableReinitialize
                initialValues={{
                  website: retailer?.website || '',
                }}
                validationSchema={validationSchema}
                onSubmit={async (values, { setSubmitting, resetForm }) => {
                  try {
                    await updateRetailer({
                      variables: {
                        updateRetailerId: retailer?.id,
                        retailer: {
                          name: retailer?.name,
                          website: values.website,
                        },
                      },
                    })

                    enqueueSnackbar(
                      I18n.t(
                        'retailerSettings.brandSettings.website.notifications.updateSuccess',
                      ),
                      { variant: 'success' },
                    )
                    setSubmitting(false)
                  } catch (err) {
                    enqueueSnackbar(
                      I18n.t(
                        'retailerSettings.brandSettings.website.notifications.updateError',
                      ),
                      { variant: 'error' },
                    )
                    setSubmitting(false)
                  }
                }}
              >
                {({ isSubmitting }) => (
                  <Form>
                    <Stack spacing={4}>
                      <Field
                        component={TextField}
                        fullWidth
                        name="website"
                        label={I18n.t(
                          'retailerSettings.brandSettings.website.title',
                        )}
                        helperText={I18n.t(
                          'retailerSettings.brandSettings.website.helperText',
                        )}
                      />
                      <Stack direction="row" justifyContent="flex-end">
                        <Button
                          type="submit"
                          disabled={isSubmitting}
                          variant="contained"
                          color="primary"
                        >
                          {I18n.t('shared.save')}
                        </Button>
                      </Stack>
                    </Stack>
                  </Form>
                )}
              </Formik>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  )
}

export default RetailerSettingsBrandSettingsWebsite
